import axios from 'axios';
import toast from 'react-hot-toast';

// Use current domain for API calls when no VITE_API_URL is set
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// P10 — rate-limit (429) UX: one throttled toast + a single automatic retry that
// respects Retry-After. General limiter is 1200/5min so 429s are rare, but the
// dashboard must degrade gracefully rather than surfacing raw axios errors.
const RATE_LIMIT_TOAST_MS = 8000;
let lastRateLimitToastAt = 0;
function notifyRateLimited(retryAfterSec: number): void {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_MS) return;
  lastRateLimitToastAt = now;
  toast.error(
    `Rate limited — retrying in ${retryAfterSec}s. Please slow down.`,
    { id: 'rate-limit', duration: RATE_LIMIT_TOAST_MS }
  );
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('opmonitor-auth');
    if (token) {
      try {
        const parsed = JSON.parse(token);
        if (parsed.state?.accessToken) {
          config.headers.Authorization = `Bearer ${parsed.state.accessToken}`;
        }
      } catch {
        // Ignore parsing errors
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 (rate limited): toast + a single retry after the server's
    // Retry-After window (P10). Retries once so transient bursts self-heal.
    if (error.response?.status === 429 && originalRequest && !originalRequest._rateRetry) {
      originalRequest._rateRetry = true;
      const headerVal = error.response.headers?.['retry-after'];
      const retryAfterSec = Math.min(60, Math.max(1, Number(headerVal) || 5));
      notifyRateLimited(retryAfterSec);
      await new Promise((resolve) => setTimeout(resolve, retryAfterSec * 1000));
      return api(originalRequest);
    }

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const stored = localStorage.getItem('opmonitor-auth');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.state?.refreshToken) {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken: parsed.state.refreshToken,
            });

            const { accessToken, refreshToken } = response.data;

            // Update stored tokens. Only overwrite the refresh token when the server
            // actually returned one — a /auth/refresh response that omits it must not
            // wipe the stored token (writing `undefined` would break the next refresh).
            parsed.state.accessToken = accessToken;
            if (refreshToken) parsed.state.refreshToken = refreshToken;
            localStorage.setItem('opmonitor-auth', JSON.stringify(parsed));

            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        }
      } catch {
        // Refresh failed, clear auth
        localStorage.removeItem('opmonitor-auth');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API Methods

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/analytics/dashboard'),
};

// Locations
export const locationApi = {
  getLive: () => api.get('/locations/live'),
  getDeviceHistory: (deviceId: string, params?: { startTime?: string; endTime?: string }) =>
    api.get(`/locations/device/${deviceId}`, { params }),
  getPlatoonHistory: (platoonId: string, params?: { startTime?: string; endTime?: string }) =>
    api.get(`/locations/platoon/${platoonId}`, { params }),
  getTrail: (deviceId: string, minutes?: number) =>
    api.get(`/locations/trail/${deviceId}`, { params: { minutes } }),
};

// SOS
export const sosApi = {
  getActive: () => api.get('/sos/active'),
  getById: (id: string) => api.get(`/sos/${id}`),
  getHistory: (params?: { startDate?: string; endDate?: string; status?: string; unitId?: string }) =>
    api.get('/sos/history', { params }),
  acknowledge: (id: string, notes?: string) =>
    api.post(`/sos/${id}/acknowledge`, { notes }),
  addResponder: (id: string, data: { responderId: string; responderType: string }) =>
    api.post(`/sos/${id}/responders`, data),
  updateResponder: (sosId: string, responderId: string, data: any) =>
    api.put(`/sos/${sosId}/responders/${responderId}`, data),
  resolve: (id: string, resolutionNotes?: string) =>
    api.post(`/sos/${id}/resolve`, { resolutionNotes }),
  resolveAll: (params?: { unitId?: string }, resolutionNotes?: string) =>
    api.post('/sos/resolve-all', { resolutionNotes }, { params }),
};

// Geofences
export const geofenceApi = {
  getAll: (params?: { type?: string; isActive?: boolean; unitId?: string; includeAllCamps?: string }) =>
    api.get('/geofences', { params }),
  getRecentEvents: (params?: { limit?: number; eventType?: string; unitId?: string }) =>
    api.get('/geofences/events/recent', { params }),
  getById: (id: string) => api.get(`/geofences/${id}`),
  createCircle: (data: any) => api.post('/geofences/circle', data),
  createPolygon: (data: any) => api.post('/geofences/polygon', data),
  update: (id: string, data: any) => api.put(`/geofences/${id}`, data),
  delete: (id: string) => api.delete(`/geofences/${id}`),
  getEvents: (id: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/geofences/${id}/events`, { params }),
  getOccupancy: (id: string) => api.get(`/geofences/${id}/occupancy`),
  check: (latitude: number, longitude: number) =>
    api.post('/geofences/check', { latitude, longitude }),
  // Field-dropped live pin → creates a HIDEOUT geofence, broadcasts geofence:hideout (contract §11.1)
  createHideout: (body: { lat: number; lng: number; name: string; platoonId: string }) =>
    api.post('/geofences/hideout', body),
};

// Alerts
export const alertApi = {
  getAll: (params?: { status?: string; severity?: string; activeOnly?: boolean; unitId?: string }) =>
    api.get('/alerts', { params }),
  getActive: () => api.get('/alerts/active'),
  getById: (id: string) => api.get(`/alerts/${id}`),
  acknowledge: (id: string) => api.post(`/alerts/${id}/acknowledge`),
  resolve: (id: string) => api.post(`/alerts/${id}/resolve`),
  acknowledgeAll: (severity?: string, unitId?: string) =>
    api.post('/alerts/acknowledge-all', {}, { params: { severity, unitId } }),
  getStats: (params?: { unitId?: string }) => api.get('/alerts/stats/summary', { params }),
};

// Analytics (election polling-centers / frequent-locations endpoints removed — API contract §10)
export const analyticsApi = {
  getDashboard: (params?: { unitId?: string }) => api.get('/analytics/dashboard', { params }),
  getAlertsByHour: (params: { startDate: string; endDate: string; unitId?: string }) =>
    api.get('/analytics/alerts-by-hour', { params }),
  getAlertsByHourType: (params: { startDate: string; endDate: string; unitId?: string }) =>
    api.get('/analytics/alerts-by-hour-type', { params }),
  getMovementSummary: (params: { startDate: string; endDate: string; platoonId?: string }) =>
    api.get('/analytics/movement-summary', { params }),
  getSOSReport: (params: { startDate: string; endDate: string }) =>
    api.get('/analytics/sos-report', { params }),
  getOperationsReport: (params: { startDate: string; endDate: string; unitId?: string }) =>
    api.get('/analytics/operations-report', { params }),
  exportData: (type: string, format: string, startDate: string, endDate: string) =>
    api.get('/analytics/export', {
      params: { type, format, startDate, endDate },
      responseType: format === 'csv' ? 'blob' : 'json',
    }),
};

// Devices
export const deviceApi = {
  getAll: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/devices', { params }),
  getById: (id: string) => api.get(`/devices/${id}`),
  update: (id: string, data: any) => api.put(`/devices/${id}`, data),
  deactivate: (id: string) => api.post(`/devices/${id}/deactivate`),
  getOnlineStats: () => api.get('/devices/stats/online'),
};

// Messages
export const messageApi = {
  getAll: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    api.get('/messages', { params }),
  markAsRead: (id: string) => api.post(`/messages/${id}/read`),
};

// Admin
export const adminApi = {
  syncAll: () => api.post('/admin/sync'),
};

// Units
export const unitApi = {
  getAll: () => api.get('/units'),
  getById: (id: string) => api.get(`/units/${id}`),
};

// Farmbases (contract §4 + docs/14 §2.1 geometry/manpower CRUD).
export const farmbaseApi = {
  getAll: (params?: { unitId?: string }) => api.get('/farmbases', { params }),
  getById: (id: string) => api.get(`/farmbases/${id}`), // includes 3-platoon rotation status
  create: (data: unknown) => api.post('/farmbases', data),
  update: (id: string, data: unknown) => api.put(`/farmbases/${id}`, data),
  delete: (id: string) => api.delete(`/farmbases/${id}`),
};

// Platoons (contract §5) — the tracked map entity, one Device each.
export const platoonApi = {
  getAll: (params?: { unitId?: string; farmbaseId?: string; phase?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/platoons', { params }),
  getLive: () => api.get('/platoons/live'),
  getById: (id: string) => api.get(`/platoons/${id}`),
  create: (data: unknown) => api.post('/platoons', data),
  update: (id: string, data: unknown) => api.put(`/platoons/${id}`, data),
  delete: (id: string) => api.delete(`/platoons/${id}`),
  // manual phase tag → logs OperationEvent, broadcasts platoon:phase (contract §5.1)
  setPhase: (id: string, body: { phase: string; lat?: number; lng?: number; note?: string }) =>
    api.post(`/platoons/${id}/phase`, body),
};

// Targets (contract §17) — first-class target lifecycle (superuser/HQ gated).
export const targetApi = {
  getAll: (params?: { status?: string; type?: string; includeExpired?: boolean; unitId?: string }) =>
    api.get('/targets', { params }),
  getById: (id: string) => api.get(`/targets/${id}`),
  create: (data: unknown) => api.post('/targets', data),
  update: (id: string, data: unknown) => api.put(`/targets/${id}`, data),
  delete: (id: string) => api.delete(`/targets/${id}`),
  assign: (id: string, body: { platoonIds: string[] }) => api.post(`/targets/${id}/assign`, body),
  adjudicate: (id: string, body: { success: boolean }) => api.post(`/targets/${id}/adjudicate`, body),
  // Execution start/stop are field/device actions (Worker C); §17 takes an empty body —
  // POST `{}` explicitly so a strict body schema accepts it.
  executionStart: (id: string) => api.post(`/targets/${id}/execution/start`, {}),
  executionStop: (id: string) => api.post(`/targets/${id}/execution/stop`, {}),
};

// Command-center reads (contract §19) — war diary, disposition, scoreboard, comms.
export const ccApi = {
  getFeed: (params?: {
    type?: string;
    unitId?: string;
    platoonId?: string;
    targetId?: string;
    since?: string;
    until?: string;
    page?: number;
    limit?: number;
  }) => api.get('/feed', { params }),
  exportFeedCsv: (params?: Record<string, string | number | undefined>) =>
    api.get('/feed', { params: { ...params, export: 'csv' }, responseType: 'blob' }),
  getForceDisposition: (params?: { unitId?: string }) => api.get('/force-disposition', { params }),
  getScoreboard: (params?: { unitId?: string }) => api.get('/scoreboard', { params }),
  getCommsStatus: (params?: { unitId?: string }) => api.get('/comms-status', { params }),
};

// Routing (§15) — road route between two points for intent lines.
// Response: { geometry:[{lat,lon}], distanceMeters, durationSeconds, steps, fallback }.
// `fallback:true` (or any error/404 before the endpoint deploys) → callers draw a straight line.
export interface RouteResponse {
  geometry: { lat: number; lon: number }[];
  distanceMeters: number;
  durationSeconds: number;
  steps?: unknown[];
  fallback?: boolean;
}

export const routeApi = {
  getRoute: (fromLat: number, fromLng: number, toLat: number, toLng: number) =>
    api.get<RouteResponse>('/route', { params: { fromLat, fromLng, toLat, toLng } }),
};

// Saved custom simulations (Simulation Center "Create a Simulation" → Save).
// DB-backed so saved sims persist across sessions/devices. `input` is the
// client-authored SimBuilderInput; the server stores it as JSON.
export const simScenarioApi = {
  getAll: () => api.get('/sim-scenarios'),
  create: (data: { name: string; input: unknown }) => api.post('/sim-scenarios', data),
  delete: (id: string) => api.delete(`/sim-scenarios/${id}`),
};

// Operations (contract §6) — nightly minor-ops cycle + AAR timeline.
export const operationApi = {
  getAll: (params?: { unitId?: string; platoonId?: string; farmbaseId?: string; night?: number; type?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/operations', { params }),
  getById: (id: string) => api.get(`/operations/${id}`), // includes phase timeline (events[]) for AAR
  create: (data: unknown) => api.post('/operations', data),
  start: (id: string) => api.post(`/operations/${id}/start`),
  complete: (id: string) => api.post(`/operations/${id}/complete`),
  abort: (id: string) => api.post(`/operations/${id}/abort`),
};

// Saved custom sim BLOCKS (Simulation Center block composer — contract docs/19
// §3.8). Owner-scoped, mirrors simScenarioApi: `def` is the client-authored
// SimBlockDef stored server-side as JSON (defJson ≤10 KB). Callers degrade
// gracefully to localStorage while the endpoint is not deployed (404).
export const simBlockApi = {
  getAll: () => api.get('/sim-blocks'),
  create: (data: { name: string; def: unknown }) => api.post('/sim-blocks', data),
  delete: (id: string) => api.delete(`/sim-blocks/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// NextGen web command platform (docs/19, docs/21) — Worker WEB additions.
// All groups below are ADD-ONLY; existing groups above are never modified.
// Response envelope for these routes is { success, data: {...} }, so callers
// read `response.data.data.<key>`.
// ═══════════════════════════════════════════════════════════════════════════

export type ReportType =
  | 'CONTACTREP' | 'SITREP' | 'SPOTREP' | 'CASEVAC'
  | 'LOCSTAT' | 'LOGREQ' | 'OBSTACLE' | 'FREETEXT';
export type ReportPriority = 'FLASH' | 'IMMEDIATE' | 'PRIORITY' | 'ROUTINE';
export type ReportStatus = 'RECEIVED' | 'ACKED';

// P1 — Field reports (docs/19 §1, §3.1). Read + ACK from the dashboard.
export const reportApi = {
  getAll: (params?: {
    type?: string; priority?: string; status?: string; platoonId?: string;
    since?: string; until?: string; page?: number; limit?: number;
  }) => api.get('/reports', { params }),
  ack: (id: string) => api.post(`/reports/${id}/ack`),
};

// P1 — Authenticated attachment streaming (no static serving, docs/19 D5).
// Fetches as a blob through the auth interceptor; caller builds an object URL.
export const attachmentApi = {
  getBlob: (id: string) => api.get(`/attachments/${id}`, { responseType: 'blob' }),
};

// P2 — Messaging threads + HQ compose (docs/19 §3.2). Complements the legacy
// messageApi group above (getAll / markAsRead) without modifying it.
export const messageThreadApi = {
  getThreads: () => api.get('/messages/threads'),
  getForPlatoon: (platoonId: string, params?: { since?: string; page?: number; limit?: number }) =>
    api.get('/messages', { params: { platoonId, ...params } }),
  // HQ → field. platoonId required; direction resolved server-side as HQ_TO_FIELD.
  send: (data: { platoonId: string; content: string; priority?: ReportPriority; ackRequired?: boolean }) =>
    api.post('/messages', data),
  markRead: (id: string) => api.post(`/messages/${id}/read`),
};

// P2 — FragO orders (docs/19 §3.3).
export const orderApi = {
  getAll: (params?: { platoonId?: string; page?: number; limit?: number }) =>
    api.get('/orders', { params }),
  issue: (data: { platoonId: string; content: string; planId?: string }) =>
    api.post('/orders', data),
};

// P3 — Operation plans (docs/19 §3.4). Block-sequence authoring + issue.
export const planApi = {
  getAll: (params?: { status?: string; unitId?: string; platoonId?: string; page?: number; limit?: number }) =>
    api.get('/operation-plans', { params }),
  getById: (id: string) => api.get(`/operation-plans/${id}`),
  create: (data: { name: string; unitId: string; platoonId?: string; blocks?: unknown[]; hHour?: string }) =>
    api.post('/operation-plans', data),
  update: (id: string, data: { name?: string; platoonId?: string | null; blocks?: unknown[]; hHour?: string | null; status?: string }) =>
    api.put(`/operation-plans/${id}`, data),
  issue: (id: string, data: { platoonId: string; hHour?: string }) =>
    api.post(`/operation-plans/${id}/issue`, data),
};

// P5 — Alert rules (docs/19 §3.5). HQ-only declarative watchdogs.
export const alertRuleApi = {
  getAll: () => api.get('/alert-rules'),
  create: (data: { kind: string; params: Record<string, unknown>; severity?: string; unitId?: string | null; enabled?: boolean }) =>
    api.post('/alert-rules', data),
  update: (id: string, data: { params?: Record<string, unknown>; severity?: string; unitId?: string | null; enabled?: boolean }) =>
    api.put(`/alert-rules/${id}`, data),
  delete: (id: string) => api.delete(`/alert-rules/${id}`),
};

// P6 — SOS responders (docs/19 §3.6). New group with the correct {platoonId}
// assign body; complements sosApi above without modifying it.
export const sosResponderApi = {
  assign: (sosId: string, platoonId: string) =>
    api.post(`/sos/${sosId}/responders`, { platoonId }),
  setStatus: (sosId: string, responderId: string, status: 'EN_ROUTE' | 'ARRIVED') =>
    api.put(`/sos/${sosId}/responders/${responderId}`, { status }),
};

// P7 — Admin console (docs/19 §3.7). HQ commanders only. Complements adminApi
// (syncAll) above without modifying it.
export const appAdminApi = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data: { email: string; firstName: string; lastName: string; role: string; unitId?: string; password: string }) =>
    api.post('/admin/users', data),
  updateUser: (id: string, data: { firstName?: string; lastName?: string; role?: string; status?: string; unitId?: string | null }) =>
    api.put(`/admin/users/${id}`, data),
  resetPassword: (id: string, password: string) =>
    api.post(`/admin/users/${id}/reset-password`, { password }),
  getDevices: () => api.get('/admin/devices'),
  setDeviceStatus: (id: string, status: string) =>
    api.put(`/admin/devices/${id}/status`, { status }),
  getAudit: (params?: { action?: string; userId?: string; since?: string; page?: number; limit?: number }) =>
    api.get('/admin/audit', { params }),
};
